import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { AppSettings } from "../../../shared/app.settings";
import { MiFormComponent } from "../../../shared/components/mi-form/mi-form.component";
import type { Country } from "../../../shared/models/country";
import type { Language } from "../../../shared/models/language";
import { CommonBindingDataService } from "../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../shared/services/entity.service";

import { AccessProviderDirective } from "app/modules/shared/directives/access-provider.directive";
import { AttributeData } from "app/modules/shared/models/attribute.models";
import { EntityResponse } from "app/modules/shared/models/entityResponse.models";
import { ConfigService } from "app/modules/shared/services/config.service";
import dayjs from "dayjs";

@Component({
    selector: 'app-add-site',
    standalone: true,
    imports: [
        BreadcrumbModule,
        TabViewModule,
        MiFormComponent,
        DialogModule,
        ButtonModule,
        TranslateModule,
        AccessProviderDirective
    ],
    templateUrl: './add-site.component.html',
    styleUrl: './add-site.component.scss'
})
export class AddSitesComponent implements OnInit {

    @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
    activeIndex: number = 0;
    data: AttributeData;
    vehicleAttributeData: Record<string, unknown>;
    routePath: MenuItem[] = [];
    attributeLabels: Record<string, string> = {};
    moduleName: string;
    country: Country;
    language: Language;
    relation: string = "oneToOne";
    cols: Array<Record<string, unknown>> = [];
    vehicleAttributeLength: number;
    siteId: string;
    siteIdForDocument: string;
    vehicleDocumentId: string;
    driverRelation: Record<string, unknown>;
    attributeValues: Record<string, unknown> = {};
    nextBtnLabel;
    previousBtnLabel;
    label: string;
    initialLabel: string = this.cs.getLabel("lbl_details");
    saveVehicleBtnLabel: string;
    isFirstTabSaved: boolean = false;
    isNew: boolean = true;
    prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
    tabCode;
    tabIndex;

    constructor(
        private vehicleService: EntityService,
        private cs: CommonBindingDataService,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute,
        private configService: ConfigService
    ) { }

    ngOnInit() {
        this.retrieveSiteId();
        this.initializeLabels();
        this.initializeVehicleService();
        this.loadSiteData();
        this.setRoutePath('details');
        this.isFirstTabSaved = false;
        this.activeIndex = 0;
    }

    private initializeLabels() {
        this.previousBtnLabel = this.cs.getLabel("lbl_previous");
        this.saveVehicleBtnLabel = this.siteId ?
            this.cs.getLabel("site.lbl_update_site") :
            this.cs.getLabel("site.lbl_save_site");
        this.nextBtnLabel = this.saveVehicleBtnLabel;
    }

    private retrieveSiteId() {
        this.siteId = this.route.snapshot.paramMap.get("id");
        if (this.siteId) {
            this.siteIdForDocument = this.siteId;
        }
    }

    private initializeVehicleService() {
        this.moduleName = AppSettings.ENTITY_CODE.SITE;
        this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
        this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
        this.vehicleService.setRelatedData([]);
    }

    private loadSiteData() {
        if (this.siteId) {
            this.vehicleService
                .getEntity(this.siteId, AppSettings.ENTITY_CODE.SITE, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
                .subscribe((res: EntityResponse) => {
                    if (res) {
                        this.processSiteResponse(res);
                        this.isNew = false;
                        const currentTab = this.vehicleAttributeData?.tabs[this.activeIndex];
                        if (currentTab) {
                            this.setRoutePath(currentTab.tabCode);
                        }
                    }
                });
        } else {
            this.getAttributes();
        }
    }

    private processSiteResponse(data: EntityResponse) {
        this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as Record<string, unknown>;
        this.normalizeManufacturingYear();
        this.processRelatedData(data.relatedData as unknown as Array<Record<string, unknown>>);
        this.getAttributes();
    }

    private normalizeManufacturingYear() {
        const yearOfMnfgAttributeCode = "year_of_manufacturing";
        if (this.attributeValues[yearOfMnfgAttributeCode]) {
            const yearValue = this.attributeValues[yearOfMnfgAttributeCode] as string;
            this.attributeValues[yearOfMnfgAttributeCode] = new Date(parseInt(yearValue), 0);
        }
    }

    private processRelatedData(relatedData: Array<Record<string, unknown>>) {
        if (relatedData.length > 0) {
            const organizedData: { [key: string]: Array<Record<string, unknown>> } = {};
            relatedData.forEach(item => {
                if (item.actionStatus === "DELETED") {
                    return;
                }
                const entityCode = item.entityCode as string;
                if (!organizedData[entityCode]) {
                    organizedData[entityCode] = [];
                }
                const entityData = this.cs.getOrgAttributeValues(item) as unknown as Record<string, unknown>;
                entityData.entityId = item.entityId;
                organizedData[entityCode].push(entityData);
            });
            this.vehicleService.setRelatedData(organizedData);
        }
    }

    getAttributes() {
        const entityType = AppSettings.ENTITY_CODE.SITE;
        const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

        this.vehicleService.getAttributeDefinition(entityType, viewCode).subscribe(res => {
            if (!res) return;
            this.setVehicleView(res);
            const currentTab = (this.vehicleAttributeData as any).tabs[this.activeIndex];
            if (currentTab) {
                this.setRoutePath(currentTab.tabCode);
            }
        });
    }

    setVehicleView(res: unknown) {
        this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
        this.vehicleAttributeData = this.cs.getOrganizedAttribute(res) as unknown as Record<string, unknown>;
        this.vehicleAttributeData.tabs = this.cs.setDependsOnAttributeCode((this.vehicleAttributeData as any)?.tabs);
        this.tabIndex = (this.vehicleAttributeData as any).tabs;
        this.tabIndex.forEach((tab: any) => {
            this.setRoutePath(tab.tabCode);
        });
        this.vehicleAttributeLength = (this.vehicleAttributeData as any).tabs.length;
        this.driverRelation = _.find((res as any)?.relations, { otherEntityCode: 'driver', relation: "oneToMany" });
    }

    setRoutePath(tabCode: unknown) {
        const routeLabel = this.siteId ? this.cs.getLabel("Edit Site") : this.cs.getLabel("Add New Site");
        if (!tabCode && (this.vehicleAttributeData as any)?.tabs?.length > 0) {
            tabCode = (this.vehicleAttributeData as any).tabs[0].tabCode;
        }

        this.routePath = [
            {
                label: `${routeLabel}`,
                routerLink: '/app/sites',
                icon: "pi pi-arrow-left",
                iconStyle: { "font-weight": "bold", "margin-right": "10px" },
            }
        ];
        if (tabCode) {
            this.routePath.push({
                label: `${tabCode}`,
                styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
                style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
            });
        }
    }

    onSaveVehicle(event) {
        const vehicleData = {
            forTenantCode: this.configService.getForTenantCode(),
            entityCode: this.moduleName,
            countryCode: this.country[0].countryCode,
            languageCode: this.language[0].langCode,
            data: [],
            relationshipData: []
        };

        const vData = event;
        const currentTab = this.vehicleAttributeData.tabs[this.activeIndex];
        const hasValidData = Object.values(vData).some(value => value !== null && value !== '');
        if (!hasValidData) {
            if (currentTab.tabCode === "specifications") {
                const currentRoute = this.router.url;
                if (currentRoute === AppSettings.ADD_VEHICLE_ROUTE) {
                    this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('vehicle.lbl_vehicle_added_successfully') });
                } else {
                    this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('vehicle.lbl_vehicle_updated_successfully') });
                }
                this.redirectToListPage();
                return;
            }
        }

        const dateIds = ["year_of_manufacturing", "issued_date", "expiry_date", "in_service_date", "out_of_service_date"];
        for (const [key, value] of Object.entries(vData)) {
            let attributeValue = <any>value;
            if (dateIds.includes(key) && key !== "year_of_manufacturing") {
                attributeValue = new Date(<string>value).getTime();
            } else if (key == "year_of_manufacturing") {
                attributeValue = Number(dayjs(<number>value).format('YYYY'))
            } else if (key === 'driver') {
                attributeValue?.map(driverId => {
                    vehicleData?.relationshipData?.push({
                        entityRelationshipConfigId: this.driverRelation.entityRelationshipConfigId,
                        otherEntityId: driverId
                    });
                })
            }

            const obj = typeof attributeValue;
            if (obj == "object") {
                if (key === 'driver') {
                    vehicleData.data.push({
                        attributeCode: key,
                        attributeValue: attributeValue ? attributeValue : []
                    });
                } else if (key === 'labels') {
                    if (!attributeValue || attributeValue.length === 0) {
                        vehicleData.data.push({
                            attributeCode: key,
                            attributeValue: [],
                        });
                    } else {
                        vehicleData.data.push({
                            attributeCode: key,
                            attributeValue,
                        });
                    }
                } else if (key === "region_geofence") {
                    if (attributeValue && attributeValue.length > 0) {
                        const valueToPush = Array.isArray(attributeValue) ? attributeValue.join(", ") : attributeValue;
                        vehicleData.data.push({
                            attributeCode: key,
                            attributeValue: valueToPush,
                        });
                    }
                } else if (key === "features") {
                    if (attributeValue && attributeValue.length > 0) {
                        vehicleData.data.push({
                            attributeCode: key,
                            attributeValue,
                        });
                    } else if (!attributeValue || attributeValue.length === 0) {
                        vehicleData.data.push({
                            attributeCode: key,
                            attributeValue: [],
                        });
                    }
                } else if (attributeValue?.length > 0) {
                    vehicleData.data.push({
                        attributeCode: key,
                        attributeValue,
                    });
                }
            } else {
                vehicleData.data.push({
                    attributeCode: key,
                    attributeValue,
                });
            }
        }

        const entitiesData = {
            countryCode: this.country[0].countryCode,
            tenantCode: this.configService.getForTenantCode(),
        };

        if (!this.siteId) {
            this.vehicleService
                .createEntities(this.moduleName, entitiesData)
                .subscribe((res: EntityResponse) => {
                    this.siteId = res.entityId;
                    if (this.siteId) {
                        this.siteIdForDocument = this.siteId;
                    }
                    this.saveVehicleData(vehicleData, true);
                },
                );
        } else {
            this.saveVehicleData(vehicleData, false);
        }
    }

    saveVehicleData(vehicleData, isNew: boolean) {
        this.isNew = isNew;
        vehicleData.data = this.cs.mapAttributeIds(vehicleData.data, this.vehicleAttributeData.tabs)
        this.vehicleService
            .saveAttributeData(vehicleData.entityCode, this.siteId, vehicleData).subscribe({
                next: () => {
                    if (isNew) {
                        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('site.lbl_site_added_successfully') });
                    } else {
                        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('site.lbl_site_updated_successfully') });
                    }
                    this.isFirstTabSaved = true;

                    setTimeout(() => {
                        if (this.isLastIndex()) {
                            this.router.navigate(["app/sites"]);
                        } else {
                            this.onNextClick();
                            this.label = this.cs.getLabel("lbl_details");
                        }
                    }, 500);
                },
                error: () => {
                    if (this.activeIndex === 0) {
                        this.isFirstTabSaved = false;
                    }
                    if (isNew) {
                        this.cs.onDisplayMessage(this.data, 'site', AppSettings.MSG_ACTION.ADD, 'error');
                    } else {
                        this.cs.onDisplayMessage(this.data, 'site', AppSettings.MSG_ACTION.UPDATE, 'error');
                    }
                }
            });
    }

    onNextBtnClick() {
        if ((this.vehicleAttributeData as any)?.tabs && (this.vehicleAttributeData as any).tabs.length > 0) {
            const currentTab = this.vehicleAttributeData.tabs[this.activeIndex];
            this.setRoutePath(currentTab.tabCode);
            if (currentTab) {
                const btn = document.getElementById(`0_${currentTab.tabCode}`);
                if (btn) {
                    btn.click();
                }
            }
        }
    }

    onPreviousClick() { }

    onNextClick() {
        if (this.activeIndex < this.vehicleAttributeLength - 1) {
            this.activeIndex++;
        }
        const currentTab = this.vehicleAttributeData.tabs[this.activeIndex];
        if (currentTab) {
            this.setRoutePath(currentTab.tabCode);
        }
        if (this.isLastIndex() === true) {
            this.nextBtnLabel = this.saveVehicleBtnLabel;
        }
    }

    getLastIndex(): number {
        return this.vehicleAttributeLength - 1;
    }

    isLastIndex(): boolean {
        return this.activeIndex === this.getLastIndex();
    }

    onCancel() {
        this.isFirstTabSaved = false;
        this.miFormComponent.resetForm();
        this.router.navigate(["/app/sites"]);
    }

    redirectToListPage() {
        this.router.navigate(["/app/sites"]);
    }
}
